/**
 * Display title resolution utility
 *
 * Single source of truth for resolving human-readable titles
 * across all components. Priority chain:
 *   1. API-fetched title (positionTitleByTicker)
 *   2. Parsed fullLabel (contains team names when available)
 *   3. Parsed shortLabel
 *   4. Raw ticker string
 */

import { parseKalshiTicker } from './parse';

/**
 * Resolve the best available display title for a ticker.
 *
 * @param ticker - Raw ticker string (e.g., "KXNBAGAME-26FEB04BOSMIA-BOS")
 * @param titleMap - API-fetched titles keyed by ticker (optional)
 * @returns Human-readable title string
 */
export function resolveDisplayTitle(ticker: string, titleMap?: Record<string, string>): string {
  // Priority 1: API-fetched title
  const apiTitle = titleMap?.[ticker];
  if (apiTitle) return apiTitle;

  // Priority 2-3: Parse the ticker for fullLabel / shortLabel
  const parsed = parseKalshiTicker(ticker);

  // Priority 2: fullLabel (contains full team names when available)
  if (parsed.fullLabel && parsed.fullLabel !== parsed.raw) {
    return parsed.fullLabel;
  }

  // Priority 3: shortLabel
  if (parsed.shortLabel && parsed.shortLabel !== parsed.raw) {
    return parsed.shortLabel;
  }

  // Priority 4: raw ticker
  return ticker;
}
